
import { defineComponent, ref, computed, watch, PropType, onMounted } from 'vue'
import { format } from 'date-fns'
import customRange from '../../../../helpers/date-range'

interface DateRange {
  startDate: Date | string
  endDate: Date | string
}

export default defineComponent({
  name: 'AppDateRangePicker',
  props: {
    modelValue: {
      type: Object as PropType<DateRange>,
      required: true
    },
    opens: {
      type: String,
      default: 'right'
    },
    appendToBody: {
      type: Boolean,
      default: true
    },
    localeData: {
      type: Object,
      default: () => ({})
    },
    ranges: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update', 'update:modelValue'],
  setup(props, { emit }) {
    const menuOpen = ref(false)

    // Internal range as array of dates for v-date-picker
    const internalRange = ref<Date[]>([])

    // Format display value for the input (using date-fns v1 syntax)
    const displayValue = computed((): string => {
      if (internalRange.value && internalRange.value.length >= 2) {
        const sorted = [...internalRange.value].sort((a, b) => a.getTime() - b.getTime())
        const start = sorted[0]
        const end = sorted[sorted.length - 1]
        if (start && end) {
          return `${format(start, 'YYYY/MM/DD')} ~ ${format(end, 'YYYY/MM/DD')}`
        }
      }
      return ''
    })

    // Convert props.modelValue to internal range format
    const syncFromProps = () => {
      if (props.modelValue) {
        const start = props.modelValue.startDate
          ? new Date(props.modelValue.startDate)
          : new Date()
        const end = props.modelValue.endDate
          ? new Date(props.modelValue.endDate)
          : new Date()
        internalRange.value = [start, end]
      }
    }

    // Watch for external changes
    watch(
      () => props.modelValue,
      () => {
        syncFromProps()
      },
      { immediate: true, deep: true }
    )

    onMounted(() => {
      syncFromProps()
    })

    // Create preset dates from ranges
    const presetDates = computed(() => {
      const rangeData = props.ranges && Object.keys(props.ranges).length > 0 
        ? props.ranges 
        : customRange()
      return Object.entries(rangeData).map(([label, dates]) => {
        const [start, end] = dates as [Date, Date]
        return {
          label,
          value: [start, end]
        }
      })
    })

    // Apply preset range
    const applyPreset = (value: Date[]) => {
      internalRange.value = value
      confirmSelection()
    }

    // Handle date change from picker
    const onDateChange = (value: Date[]) => {
      // v-date-picker with multiple="range" returns all selected dates
      // We keep only the first and last
      if (value && value.length >= 2) {
        const sorted = [...value].sort((a, b) => a.getTime() - b.getTime())
        internalRange.value = [sorted[0], sorted[sorted.length - 1]]
      } else {
        internalRange.value = value || []
      }
    }

    // Confirm and emit selection
    const confirmSelection = () => {
      if (internalRange.value && internalRange.value.length >= 2) {
        const sorted = [...internalRange.value].sort((a, b) => a.getTime() - b.getTime())
        const newRange: DateRange = {
          startDate: sorted[0],
          endDate: sorted[sorted.length - 1]
        }
        emit('update:modelValue', newRange)
        emit('update', newRange)
      }
      menuOpen.value = false
    }

    return {
      menuOpen,
      internalRange,
      displayValue,
      presetDates,
      applyPreset,
      onDateChange,
      confirmSelection
    }
  }
})
